import { useEffect, useState } from "react";

// Firebase Storage doesn't reliably set long-lived Cache-Control headers, so
// the same product/store image can hit the network again on every visit —
// wasteful on a mobile app that's constantly backgrounded/reopened and
// re-shows the same handful of images (browse -> store -> cart -> checkout).
// This caches the actual bytes via the Cache Storage API, which — unlike an
// in-memory cache — survives the app being closed and reopened.
const CACHE_NAME = "teco-images-v1";

// Session-scoped: once a src has been resolved to an object URL this tab,
// reuse it instead of re-deriving one from the cached blob on every mount.
const objectUrlCache = new Map();
// Dedupes concurrent requests for the same src (e.g. the same product
// rendered in both the cart drawer and checkout at once).
const inFlight = new Map();

async function resolveCachedSrc(src) {
  if (objectUrlCache.has(src)) return objectUrlCache.get(src);
  if (inFlight.has(src)) return inFlight.get(src);

  const promise = (async () => {
    try {
      if (!("caches" in window)) return src; // Cache Storage unsupported — use the network URL as-is
      const cache = await caches.open(CACHE_NAME);
      let response = await cache.match(src);
      if (!response) {
        const networkResponse = await fetch(src);
        if (!networkResponse.ok) return src;
        await cache.put(src, networkResponse.clone());
        response = networkResponse;
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      objectUrlCache.set(src, objectUrl);
      return objectUrl;
    } catch {
      return src; // caching failed for any reason — fall back, never block rendering
    } finally {
      inFlight.delete(src);
    }
  })();

  inFlight.set(src, promise);
  return promise;
}

/**
 * Returns a display-ready <img> src for `src`, transparently cached via the
 * Cache Storage API. Renders the original URL immediately (first paint, and
 * whenever caching is unsupported/still in flight/fails), then swaps to the
 * cached copy once resolved — so this can never make an image slower or
 * break rendering, only make repeat loads faster.
 */
export function useCachedImageSrc(src) {
  const [resolvedSrc, setResolvedSrc] = useState(() => (src ? objectUrlCache.get(src) || src : src));

  useEffect(() => {
    if (!src) {
      setResolvedSrc(src);
      return;
    }

    setResolvedSrc(objectUrlCache.get(src) || src);
    let cancelled = false;

    resolveCachedSrc(src).then((result) => {
      if (!cancelled) setResolvedSrc(result);
    });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return resolvedSrc;
}
